import React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStatus } from '@/hooks/useApi';
import { api } from '@/services/api';
import { useModuleContext } from '@/contexts/ModuleContext';
import { NotificationBell } from '@/components/notifications';
import { cn } from '@/utils/cn';
import UserMenu from './UserMenu';
import SidebarContext from './SidebarContext';
import SearchTrigger from '@/components/search/SearchTrigger';
import { NAV_ACTIVE, NAV_BG, NAV_HOVER, NAV_MUTED, NAV_TEXT } from './navTheme';
import {
  APPROVALS, HELP, SETTINGS, activeItem, activePath, notePage, buildNav, configuredHideNav, isGroupActive, type NavEntry, type NavGroup, type NavItem,
  type ReportLink,
} from './navConfig';

/** C5: how many items wait for the user's approval (GET /api/approvals/count), refreshed every minute. A pill after
 *  the label; collapsed, a dot on the icon. Nothing while it's 0 or unknown. */
function ApprovalsBadge({ collapsed }: { collapsed: boolean }) {
  const q = useQuery<{ total: number }>({
    queryKey: ['approvals-count'], queryFn: () => api.get('/api/approvals/count').then(r => r.data),
    refetchInterval: 60_000, staleTime: 30_000,
  });
  const n = q.data?.total ?? 0;
  if (!n) return null;
  return collapsed
    ? <span className="absolute -right-1.5 -top-1.5 h-2.5 w-2.5 rounded-full bg-mp-nav-warning ring-2 ring-mp-nav-bg" aria-label={`${n} waiting for your approval`} />
    : <span className="ml-auto rounded-full bg-mp-nav-warning px-1.5 text-mp-sm font-semibold leading-5 text-mp-nav-bg tabular-nums"
            aria-label={`${n} waiting for your approval`}>{n > 99 ? '99+' : n}</span>;
}

/** Customer Apply P2: Apply proposals waiting for approval in the user's companies (GET /api/cashapp/apply-proposals/count),
 *  as the approvals pill. */
function ApplyProposalsBadge({ collapsed }: { collapsed: boolean }) {
  const q = useQuery<{ submitted: number; to_approve?: number }>({
    queryKey: ['apply-proposals-count'],
    queryFn: () => api.get('/api/cashapp/apply-proposals/count').then(r => r.data),
    refetchInterval: 60_000, staleTime: 30_000, retry: false,
  });
  // round 3 (1 Nov): only those this user can approve (never their own)
  const n = q.data?.to_approve ?? q.data?.submitted ?? 0;
  if (!n) return null;
  return collapsed
    ? <span className="absolute -right-1.5 -top-1.5 h-2.5 w-2.5 rounded-full bg-mp-nav-warning ring-2 ring-mp-nav-bg" aria-label={`${n} Apply proposals waiting for approval`} />
    : <span className="ml-auto rounded-full bg-mp-nav-warning px-1.5 text-mp-sm font-semibold leading-5 text-mp-nav-bg tabular-nums"
            aria-label={`${n} Apply proposals waiting for approval`}>{n > 99 ? '99+' : n}</span>;
}

/** The Inbox's pill (2 Nov switch-on): emails held for a person plus customer emails that need a reply, each email once
 *  (GET /api/cashapp/email-intake/review/count; nothing while reading runs in trial, delivering false). The route
 *  refuses a user limited to some companies, as the Inbox does: then nothing shows. */
export function InboxBadge({ collapsed }: { collapsed: boolean }) {
  // 3 Nov re-test (44 here, 43 in Held): the Inbox's own counts, held and needing a reply, each email once
  const held = useQuery<{ held: number; needs_reply?: number; delivering?: boolean }>({
    queryKey: ['cashapp-held-count'],
    queryFn: () => api.get('/api/cashapp/email-intake/review/count').then(r => r.data),
    refetchInterval: 60_000, staleTime: 30_000, retry: false,
  });
  const n = (held.data && held.data.delivering !== false ? held.data.held ?? 0 : 0) + (held.data?.needs_reply ?? 0);
  if (!n) return null;
  return collapsed
    ? <span className="absolute -right-1.5 -top-1.5 h-2.5 w-2.5 rounded-full bg-mp-nav-warning ring-2 ring-mp-nav-bg" aria-label={`${n} emails need a person`} />
    // 3 Nov (R320 browser round: 54 here, 68 on the Inbox's Needs attention): the pill is Held and Needs a reply; the
    // Inbox's Needs attention also lists the emails still to triage, and says so
    : <span className="ml-auto rounded-full bg-mp-nav-warning px-1.5 text-mp-sm font-semibold leading-5 text-mp-nav-bg tabular-nums"
            aria-label={`${n} emails need a person`}
            title={`${n} held or waiting for a reply. The Inbox's Needs attention also lists emails still to triage.`}>{n > 99 ? '99+' : n}</span>;
}

export function reportLinks(): ReportLink[] {
  const links = typeof window !== 'undefined' ? window.APP_CONFIG?.REPORT_LINKS : undefined;
  return Array.isArray(links) ? links.filter(l => l && l.label && l.url) : [];
}

const LONG_LABEL = 22;
/** C67: the menu's icons (18px, with 14px type and 32px rows), and the indent that puts a group's items under its
 *  label (row padding 12 + icon 18 + gap 12). */
const ICON = 'h-[18px] w-[18px] shrink-0';
const INDENT = 'pl-[42px]';

const focusRing = 'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mp-accent';

// Look B makes tables dense; the menu has its own --mp-nav-* sizes (14px type, 12px padding, a 2px rhythm; compact
// menu, 30 Oct: 32px rows and 12px above a titled section, so Cash application open fits at 1366x768).
const rowClass = (active: boolean, collapsed = false) => cn(
  'flex h-mp-nav-item w-full items-center gap-mp-nav-x rounded-md text-mp-nav-size transition-colors', focusRing,
  collapsed ? 'justify-center px-2' : 'px-mp-nav-x',
  // the active page: a tinted row with the accent bar on its left edge
  active ? cn(NAV_ACTIVE, 'font-medium shadow-[inset_3px_0_0_var(--mp-nav-accent)]') : cn(NAV_MUTED, NAV_HOVER),
);

function ItemLink({ item, active, collapsed = false, onNavigate, className, role, indent = false }: {
  item: NavItem; active: boolean; collapsed?: boolean; onNavigate?: () => void; className?: string; role?: string;
  indent?: boolean;
}) {
  const Icon = item.icon;
  const cls = className ?? cn(rowClass(active, collapsed), indent && INDENT);
  const label = collapsed ? item.label : undefined;
  const title = collapsed || item.label.length > LONG_LABEL ? item.label : undefined;   // a cut label shows in full
  const badge = item.href === APPROVALS.href ? <ApprovalsBadge collapsed={collapsed} />
    : item.href === '/cashapp/apply-proposals' ? <ApplyProposalsBadge collapsed={collapsed} />
    : item.href === '/cashapp/inbox' ? <InboxBadge collapsed={collapsed} /> : null;
  const body = <><span className="relative inline-flex shrink-0"><Icon className={ICON} aria-hidden />{collapsed && badge}</span>
    {!collapsed && <span className="truncate">{item.label}</span>}{!collapsed && badge}</>;
  if (item.external) {
    return (
      <a href={item.href} target="_blank" rel="noopener noreferrer" role={role} className={cls} onClick={onNavigate}
         aria-label={label} title={title}>{body}</a>
    );
  }
  return (
    // Link, not NavLink: NavLink sets aria-current by its own prefix match (on /cashapp/remittances the Workbench too);
    // the one current item is the sidebar's to say (activeItem / activePath)
    <Link to={item.href} role={role} aria-current={active ? 'page' : undefined} className={cls} onClick={onNavigate}
          aria-label={label} title={title}>{body}</Link>
  );
}

/** Compact menu (30 Oct, Davin): a group's titled sections fold away. Each one's state is kept per user in the
 *  browser (best effort: without storage they just start as the defaults). By default every section is folded but the
 *  one holding the current page (Davin: "on that additonal small change please do yes"), so Cash application open fits
 *  at 1366x768; a section the user opened or folded stays as they left it. */
export const sectionsKey = (user: string) => `mp.sidebar.sections.${user}`;
const sectionId = (group: string, title: string) => `${group}:${title}`;

function useFoldedSections(user: string | undefined): [Record<string, boolean>, (id: string, folded: boolean) => void] {
  const read = React.useCallback((): Record<string, boolean> => {
    if (!user) return {};
    try {
      const v = JSON.parse(window.localStorage.getItem(sectionsKey(user)) || '{}');
      return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
    } catch { return {}; }
  }, [user]);
  const [folded, setFolded] = React.useState(read);
  React.useEffect(() => { setFolded(read()); }, [read]);
  const set = React.useCallback((id: string, f: boolean) => setFolded(prev => {
    const next = { ...prev, [id]: f };
    if (user) {
      try { window.localStorage.setItem(sectionsKey(user), JSON.stringify(next)); } catch { /* not kept */ }
    }
    return next;
  }), [user]);
  return [folded, set];
}

/** Expanded sidebar: a group is a disclosure (its button toggles the list beneath). An accordion: one group open at
 *  a time, and on every navigation the current page's group (and only it) is open. Inside it, each titled section is
 *  a disclosure too; the one holding the current page is always open (its title isn't a toggle there). */
function GroupSection({ group, open, onToggle, groupActive, active, onNavigate, folded, setFolded }: {
  group: NavGroup; open: boolean; onToggle: () => void; groupActive: boolean; active: NavItem | null;
  onNavigate?: () => void; folded: Record<string, boolean>; setFolded: (id: string, folded: boolean) => void;
}) {
  const Icon = group.icon;
  const listId = `sidebar-group-${group.id}`;
  return (
    <div data-nav-group={group.id}>
      <button type="button" aria-expanded={open} aria-controls={listId} onClick={onToggle}
              className={cn(rowClass(false), groupActive && 'font-medium text-mp-nav-active-text')}>
        <Icon className={ICON} aria-hidden />
        <span className="flex-1 truncate text-left">{group.label}</span>
        <ChevronDown className={cn('h-4 w-4 shrink-0 transition-transform', !open && '-rotate-90')} aria-hidden />
      </button>
      {open && (
        <div id={listId} className="mt-mp-nav-gap space-y-mp-nav-gap">
          {group.sections.map((s, si) => {
            const sid = s.title ? sectionId(group.id, s.title) : '';
            const here = !!active && s.items.includes(active);
            const shown = !s.title || here || folded[sid] === false;
            const itemsId = `${listId}-${si}`;
            return (
              <div key={s.title ?? si} role="group" aria-label={s.title ?? group.label} className="space-y-mp-nav-gap"
                   data-section={s.title}>
                {s.title && (
                  // one 32px row (a hit target), the title at its foot: the space above it is the section gap
                  <button type="button" aria-expanded={shown} aria-controls={itemsId}
                          aria-disabled={here || undefined} title={here ? 'The page you are on is in this list' : undefined}
                          onClick={() => { if (!here) setFolded(sid, shown); }}
                          className={cn('flex h-mp-nav-item w-full items-end gap-1 rounded-md pb-1 pr-3 text-left text-[11px] font-semibold uppercase tracking-wider transition-colors',
                                        focusRing, INDENT, NAV_MUTED, !here && 'hover:text-mp-nav-active-text', here && 'cursor-default')}>
                    <span className="flex-1 truncate">{s.title}</span>
                    {!here && <ChevronDown className={cn('mb-px h-3.5 w-3.5 shrink-0 transition-transform', !shown && '-rotate-90')} aria-hidden />}
                  </button>
                )}
                {shown && (
                  <div id={itemsId} className="space-y-mp-nav-gap">
                    {s.items.map(item => (
                      <ItemLink key={item.href} item={item} active={item === active} onNavigate={onNavigate} indent />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** A group whose menu opens to the right: every group in the collapsed sidebar (an icon). C67: the expanded menu
 *  has no pop-outs (every group there is a drop-down); `labelled` remains for a labelled row should one be wanted. Enter, Space or ArrowDown open it; arrows move; Esc closes it back to
 *  its button; Tab or a click outside closes it. It is positioned against the window (the scrolling nav doesn't clip
 *  it) and kept inside it; a group with several titled sections lays them out in two columns. */
function GroupFlyout({ group, open, setOpen, groupActive, active, labelled = false }: {
  group: NavGroup; open: boolean; setOpen: (id: string | null) => void; groupActive: boolean; active: NavItem | null;
  labelled?: boolean;
}) {
  const button = React.useRef<HTMLButtonElement>(null);
  const menu = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);
  const Icon = group.icon;
  const items = () => Array.from(menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);

  const twoColumns = group.sections.filter(s => s.title).length >= 4;
  React.useLayoutEffect(() => {
    if (!open || !button.current) return;
    const r = button.current.getBoundingClientRect();
    const h = menu.current?.offsetHeight ?? 320;
    setPos({ top: Math.max(8, Math.min(r.top, window.innerHeight - h - 8)), left: r.right + 8 });
  }, [open]);
  React.useEffect(() => { if (open) items()[0]?.focus(); }, [open, pos]);

  const onButtonKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(group.id); }
  };
  const onMenuKey = (e: React.KeyboardEvent) => {
    const list = items();
    const i = list.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); list[(i + 1) % list.length]?.focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); list[(i - 1 + list.length) % list.length]?.focus(); }
    else if (e.key === 'Home') { e.preventDefault(); list[0]?.focus(); }
    else if (e.key === 'End') { e.preventDefault(); list[list.length - 1]?.focus(); }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(null); button.current?.focus(); }
    else if (e.key === 'Tab') { setOpen(null); }
  };

  return (
    <div data-nav-group={group.id}>
      {labelled ? (
        // the current page is inside, out of sight: the group row itself carries the active bar
        <button ref={button} type="button" aria-haspopup="menu" aria-expanded={open}
                onClick={() => setOpen(open ? null : group.id)} onKeyDown={onButtonKey}
                className={cn(rowClass(groupActive), open && !groupActive && 'bg-mp-nav-active-bg text-mp-nav-active-text')}>
          <Icon className={ICON} aria-hidden />
          <span className="flex-1 truncate text-left">{group.label}</span>
          <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
        </button>
      ) : (
        <button ref={button} type="button" aria-haspopup="menu" aria-expanded={open} aria-label={group.label}
                title={group.label} onClick={() => setOpen(open ? null : group.id)} onKeyDown={onButtonKey}
                className={rowClass(groupActive || open, true)}>
          <Icon className={ICON} aria-hidden />
        </button>
      )}
      {open && (
        <div ref={menu} role="menu" aria-label={group.label} onKeyDown={onMenuKey}
             style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}
             data-columns={twoColumns ? 2 : 1}
             className={cn('fixed z-[100] max-h-[calc(100vh-16px)] overflow-y-auto rounded-md border border-mp-border bg-mp-surface py-1.5 font-mp-sans shadow-lg',
                           twoColumns ? 'w-[30rem]' : 'w-64')}>
          <div className="px-mp-nav-x pb-1 pt-2 text-mp-nav-size font-semibold text-mp-text">{group.label}</div>
          <div className={cn(twoColumns && 'columns-2 gap-0')}>
          {group.sections.map((s, si) => (
            <div key={s.title ?? si} role="group" aria-label={s.title ?? group.label}
                 className={cn('space-y-mp-nav-gap break-inside-avoid', si > 0 && !twoColumns && 'mt-1.5 border-t border-mp-border pt-1.5',
                               twoColumns && 'pb-1.5')}>
              {s.title && <div className="px-mp-nav-x pb-1 pt-mp-nav-section text-[11px] font-semibold uppercase tracking-wider text-mp-text-muted">{s.title}</div>}
              {s.items.map(item => (
                <ItemLink key={item.href} item={item} role="menuitem" active={item === active}
                          onNavigate={() => setOpen(null)}
                          className={cn('flex h-mp-nav-item items-center gap-mp-nav-x px-mp-nav-x text-mp-nav-size hover:bg-mp-surface-2 focus:bg-mp-surface-2 focus:outline-none',
                                        item === active ? 'font-medium text-mp-text' : 'text-mp-text-muted')} />
              ))}
            </div>
          ))}
          </div>
        </div>
      )}
    </div>
  );
}

export interface SidebarProps {
  /** Icons only (the desktop rail's collapsed state). Never in the drawer. */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** The phone drawer: a close button instead of the collapse toggle. */
  variant?: 'rail' | 'drawer';
  onClose?: () => void;
  /** Called on following a link (the drawer closes). */
  onNavigate?: () => void;
}

/** The app's navigation: brand, subsidiary and period, the modules this user may use (module access, roles and
 *  config.js HIDE_NAV, as buildNav decides), then Help, notifications and the user menu at the foot. */
const Sidebar: React.FC<SidebarProps> = ({ collapsed = false, onToggleCollapse, variant = 'rail', onClose, onNavigate }) => {
  const location = useLocation();
  const { data: authStatus } = useAuthStatus();
  const { hasModule, isLoading: modulesLoading } = useModuleContext();
  const [openGroup, setOpenGroup] = React.useState<string | null>(null);           // collapsed: the open flyout
  // expanded: the one open group (an accordion, so the longest group never pushes the menu into a scroll).
  // undefined = the current page's group; null = all closed. Reset on every navigation.
  const [expandedGroup, setExpandedGroup] = React.useState<string | null | undefined>(undefined);
  const [folded, setFolded] = useFoldedSections(authStatus?.user ? String(authStatus.user.id ?? authStatus.user.username) : undefined);

  const roles = authStatus?.user?.roles ?? [];
  const isAdmin = roles.includes('admin');
  // D17 (UX review): until the instance's modules are known, no menu (drawing the pre-module menu and then swapping
  // it was a visible jump on every load)
  // the Stock menu's client facts (the same query as the Stock pages): Parallel run only where the client had one
  const stock = useQuery<{ parallel_run?: boolean }>({ queryKey: ['sbl', 'client'], staleTime: Infinity,
    enabled: !modulesLoading && hasModule('sbl'), queryFn: () => api.get('/api/sbl/client').then(r => r.data) });
  const hide = [...configuredHideNav(roles), ...(stock.data?.parallel_run === false ? ['/sbl/parallel-run'] : [])];
  const entries: NavEntry[] = modulesLoading ? []
    : buildNav({ hasModule, isAdmin, roles, reportLinks: reportLinks(), hideNav: hide });
  // the path to highlight: a page outside the menu (a customer) lights up the page it was opened from
  const here = activePath(location.pathname, (location.state as { from?: unknown } | null)?.from);
  const active = activeItem(here, entries);
  const main = entries.filter(e => !(e.kind === 'link' && (e.item === HELP || e.item === SETTINGS)));
  const help = entries.find(e => e.kind === 'link' && e.item === HELP);
  const settings = entries.some(e => e.kind === 'link' && e.item === SETTINGS);

  React.useEffect(() => {
    if (!openGroup) return;
    const onClick = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest(`[data-nav-group="${openGroup}"]`)) setOpenGroup(null);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [openGroup]);
  React.useEffect(() => { setOpenGroup(null); }, [location.pathname, collapsed]);
  React.useEffect(() => { notePage(location.pathname, location.search); }, [location.pathname, location.search]);   // 31 Oct review #17
  React.useEffect(() => { setExpandedGroup(undefined); }, [location.pathname]);

  const drawer = variant === 'drawer';
  const iconOnly = collapsed && !drawer;

  return (
    <div className={cn('flex h-full flex-col border-r border-mp-nav-border font-mp-sans text-mp-nav-size', NAV_BG, NAV_TEXT)}
         data-testid="sidebar" data-collapsed={iconOnly ? 'true' : 'false'}>
      <div className={cn('flex shrink-0 items-center border-b border-mp-nav-border',
                         iconOnly ? 'flex-col gap-2 py-3' : 'h-12 justify-between gap-2 px-3')}>
        {/* C3: the white logo straight on the dark band, as Davin wants (no tile). 480x129 shown 28px high: crisp at 2x */}
        <NavLink to="/dashboard" onClick={onNavigate} aria-label="MatchPoint home"
                 className={cn('flex items-center rounded-md', focusRing, iconOnly ? 'p-1' : 'px-1 py-1')}>
          <img src={iconOnly ? '/assets/clarative-icon.png' : '/assets/clarative-480.png'} alt=""
               className={iconOnly ? 'h-7 w-7' : 'h-7 w-auto'} />
        </NavLink>
        {drawer ? (
          <button type="button" onClick={onClose} aria-label="Close menu"
                  className={cn('rounded-md p-2', NAV_MUTED, NAV_HOVER, focusRing)}>
            <X className="h-5 w-5" />
          </button>
        ) : onToggleCollapse && (
          <button type="button" onClick={onToggleCollapse} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  aria-pressed={collapsed} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  className={cn('rounded-md p-2', NAV_MUTED, NAV_HOVER, focusRing)}>
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        )}
      </div>

      {/* ⌘K: find any account, customer, invoice, amount or page (UX Stage C2) */}
      <div className={cn('border-b border-mp-nav-border', iconOnly ? 'flex justify-center py-2' : 'px-2 py-1.5')}>
        <SearchTrigger compact={iconOnly} />
      </div>

      <SidebarContext collapsed={iconOnly} onExpand={onToggleCollapse} />

      {/* every row evenly spaced (--mp-nav-gap); the section gap only above a titled section and above the foot */}
      <nav aria-label="Main navigation" className="flex-1 space-y-mp-nav-gap overflow-y-auto px-2 pb-2 pt-2">
        {main.map(e => {
          if (e.kind === 'link') {
            return <ItemLink key={e.item.href} item={e.item} active={e.item === active} collapsed={iconOnly} onNavigate={onNavigate} />;
          }
          const g = e.group;
          const groupActive = isGroupActive(g, here, active);
          // C67: pop-outs only in the icon rail; expanded (and in the drawer) every group is a drop-down
          if (iconOnly) {
            return <GroupFlyout key={g.id} group={g} open={openGroup === g.id} setOpen={setOpenGroup}
                                groupActive={groupActive} active={active} />;
          }
          const open = expandedGroup === undefined ? groupActive : expandedGroup === g.id;
          return <GroupSection key={g.id} group={g} open={open} groupActive={groupActive} active={active}
                               onNavigate={onNavigate} onToggle={() => setExpandedGroup(open ? null : g.id)}
                               folded={folded} setFolded={setFolded} />;
        })}
        {settings && (
          // C67: Admin's pages live on the Settings hub; one item at the foot of the nav
          <div className="mt-mp-nav-section border-t border-mp-nav-border pt-mp-nav-section">
            <ItemLink item={SETTINGS} active={SETTINGS === active} collapsed={iconOnly} onNavigate={onNavigate} />
          </div>
        )}
      </nav>

      {authStatus?.user && (
        // one row (nav compact): the user, then Help and notifications as icons; stacked when collapsed
        <div className={cn('flex shrink-0 items-center gap-1 border-t border-mp-nav-border px-2 py-1.5', iconOnly && 'flex-col')}>
          <div className={cn('min-w-0', !iconOnly && 'flex-1')}><UserMenu collapsed={iconOnly} /></div>
          {help && help.kind === 'link' && (
            <ItemLink item={help.item} active={help.item === active} collapsed onNavigate={onNavigate}
                      className={cn(rowClass(help.item === active, true), 'w-auto')} />
          )}
          <NotificationBell />
        </div>
      )}
    </div>
  );
};

export default Sidebar;
